import { QueryFunctionContext } from '@tanstack/react-query';
import _ from 'lodash';
import { api } from '../services/api';
import { IData, IDataNoPagination } from '../types/shared';

export type NivelEmpresa = 'Latão' | 'Prata' | 'Ouro' | 'Diamante';

export interface IEmpresaGov {
  codi_emp?: number;
  external_id?: string;
  cnpj?: string;
  razao_social?: string;
  nome_fantasia?: string;
  data_cadastro?: string;
  natureza_juridica?: string;
  porte?: string;
  porte_sigla?: string;
  status?: 'Ativa' | 'Inativa';
  data_status?: string;
  matriz?: 'Sim' | 'Nao';
  atualizado_em?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  pais?: string;
  cnae_principal_cod?: string;
  cnae_principal_desc?: string;
  cnae_secundario?: string;
}

export interface ICompany {
  id: 0;
  dt_criacao: any;
  dt_atualizacao: any;
  nome: string;
  cnpj: string;
  razao_social: string;
  external_id: string;
  external_id_column: string;
  quebras: number;
  sem_quebras: number;
  nf_quebradas: any;
  quantidade_notas: number;
  variacao_faturamento: number;
  faturamento_anterior: number;
  faturamento_atual: number;
  impostos_guia: IImposto;
  impostos_calculo: IImposto;
  calc_manual: IImposto;
  cidade: any;
  uf: any;
  dt_cadastro: any;
  dt_cli_desde: any;
  dt_cli_ate: any;
  ativa: any;
  grupo_de_empresas: any;
  tags: any;
  comercial: any;
  contabil: any;
  financeiro: any;
  contas_a_pagar: any;
  encerramento_anual: any;
  fiscal: any;
  imposto_de_renda: any;
  integracao_contabil: any;
  integracao_fiscal: any;
  pessoal: any;
  relacionamento_com_cliente: any;
  societario: any;
  resp_comercial: any;
  resp_contabil: any;
  resp_financeiro: any;
  resp_contas_a_pagar: any;
  resp_encerramento_anual: any;
  resp_fiscal: any;
  resp_imposto_de_renda: any;
  resp_integracao_contabil: any;
  resp_integracao_fiscal: any;
  resp_pessoal: any;
  resp_relacionamento_com_cliente: any;
  resp_societario: any;
  quantidade_cte: number;
  quantidade_nfe: number;
  quantidade_nfce: number;
  quantidade_nfse: number;
  receitas?: number;
  despesas?: number;
  lucro_ou_prejuizo?: string;
  porcentagem_lucro?: number;
  regime_tributario_atualizado?: string;
  qt_notas_dominio?: any;
  qt_notas_sieg?: any;
  qt_notas_autmais?: any;
  qt_notas_portal_nacional?: any;
  porcentagem_emprestimos?: number;
  qt_apuracoes?: any;
  paga_por_quotas?: boolean;
  periodicidade_irpj_csll?: 'mensal' | 'trimestral';
  mes_no_trimestre?: number;
  is_fechamento_trimestral?: boolean;
  /** Supervisor LP/LR: receita financeira que o Domínio somou no faturamento. */
  receitas_financeiras?: {
    total: number;
    descontado: number;
    acumuladores: { codigo: number | null; nome: string | null; valor: number }[];
  } | null;
  /** Idem, já fora do faturamento no Domínio (só informativo). */
  receitas_financeiras_fora?: {
    total: number;
    acumuladores: { codigo: number | null; nome: string | null; valor: number }[];
  } | null;
  /** Supervisor LP/LR: motivo provável das divergências com o Domínio. */
  motivos_divergencia?: IMotivosDivergencia | null;
  nivel?: NivelEmpresa;
  empresa_gov?: IEmpresaGov;

  // Indicadores de divergência (calculados pelo backend)
  div_cnpj_invalido?: boolean;
  div_razao_social?: boolean;
  div_cnpj?: boolean;
  div_cidade?: boolean;
  div_uf?: boolean;

  // Dados do governo para comparação
  gov_razao_social?: string;
  gov_cnpj?: string;
  gov_cidade?: string;
  gov_uf?: string;

  // Logo
  logo_s3_key?: string;
  logo_url?: string;

  // Campos dinâmicos (valores atribuídos via "editar características")
  campos_valores?: Array<{ campo: string; value: string }>;
}

export interface ICompaniesSituations {
  ok: number;
  aviso: number;
  erro: number;
  outros?: number;
  total_com_faturamento?: number;
  total_sem_faturamento?: number;
  total_empresas?: number;
}

// Interface para request do cálculo IA
export interface ICalculoIARequest {
  empresa_id: number;
  external_id: string;
  faturamento: number;
  anexo?: number;
  secao?: number;
  tabela?: number;
  rbt12?: number;
  vl_servico?: number;
  vl_saida?: number;
  vl_outros?: number;
  date_start: string;
  date_end: string;
}

// Interface para response do cálculo IA
export interface ICalculoIAResponse {
  empresa_id: number;
  faixa?: number;
  aliquota_efetiva?: number;
  valor_imposto?: number;
  rbt12?: number;
  aliq_nominal?: number;
  parcela_deduzir?: number;
  anexo?: number | string;
  info_partilha?: string;
  segregado?: boolean;
  detalhes_segregacao?: any[];
  erro?: string;
  cache?: boolean;
}

export interface IApiSiegTotais {
  total_empresas: number;
  empresas_saida: number;
  empresas_saida_pct: number;
  empresas_entrada: number;
  empresas_entrada_pct: number;
  empresas_s_saida: number;
  empresas_s_saida_pct: number;
  empresas_s_entrada: number;
  empresas_s_entrada_pct: number;
  ok_saida: number;
  ok_saida_pct: number;
  ok_entrada: number;
  ok_entrada_pct: number;
  erro_saida: number;
  erro_saida_pct: number;
  erro_entrada: number;
  erro_entrada_pct: number;
  aviso_saida: number;
  aviso_saida_pct: number;
  aviso_entrada: number;
  aviso_entrada_pct: number;
  com_dados: number;
  com_dados_pct: number;
  sem_dados: number;
  sem_dados_pct: number;
}

export interface IHistoricoCalculoIA {
  key: string; // "empresa_id-faturamento-anexo-faixa-timestamp"
  valor: string; // alíquota efetiva como string (ex: "8.54")
}

export interface ICalculoIA {
  faixa?: number;
  aliquota_efetiva?: number;
  valor_imposto?: number;
  rbt12?: number;
  aliq_nominal?: number;
  parcela_deduzir?: number;
  anexo?: number;
  info_partilha?: string;
  cache?: boolean;
  detalhamento?: string;
  erro?: string;
  apuracoes?: IAtividadeSN[];
}

// Interface para cada atividade do Simples Nacional
export interface IAtividadeSN {
  anexo: number;
  secao?: number | null;
  tabela?: number | null;
  valor_guia: number;
  valor_ia: number;
  faixa?: number | null;
  aliquota_efetiva?: number | null;
  info_partilha?: string | null;
}

export interface IImposto {
  cofins: number;
  csll: number;
  icms: number;
  irpj: number;
  pis: number;
  simples_nacional?: number;
  vl_simples_nacional?: number;
  anexo_simples_nacional?: number;
  secao_simples_nacional?: number;
  tabela_simples_nacional?: number;
  simples_nacional_pdf?: string;
  simples_nacional_prct?: number;
  irpj_pdf?: string;
  csll_pdf?: string;
  cofins_pdf?: string;
  pis_pdf?: string;
  icms_pdf?: string;
  /** Impostos com PDF de guia gravado que não foi achado no S3. */
  pdf_nao_encontrado?: string[];
  // DIFAL/retenções: dado bruto do Domínio (extract_impostos), sem PDF de
  // guia — card adicional, não substitui o card ICMS existente (ver
  // conferencia_fiscal/lucro_presumido_real.py, achado real empresa 1117:
  // tb_empresa_imposto classifica DIFAL como "ICMS" por falta de categoria).
  difal?: number;
  retencoes?: number;
  /** Retido pelo tomador nas NFS-e prestadas (XML) — abate do que a empresa paga. */
  retencoes_sofridas?: number;
  vl_saida?: number;
  vl_servico?: number;
  vl_outros?: number;
  cofins_prct?: number;
  csll_prct?: number;
  icms_prct?: number;
  irpj_prct?: number;
  pis_prct?: number;
  difal_prct?: number;
  retencoes_prct?: number;
  total_faturamento?: number;
  rbt12_ecac?: number;
  rbt12_ecac_path?: string;
  faturamento_acumulado?: number;
  historico_calculos_ia?: IHistoricoCalculoIA[];
  calculo_ia?: ICalculoIA;
  apuracoes?: IAtividadeSN[];
  detalhes?: {
    faturamento_acumulado: number;
    periodicidade: string;
    mes_no_trimestre: number;
    vl_saida_tri: number;
    vl_servico_tri: number;
    base_irpj: number;
    base_csll: number;
    irpj_adicional: number;
    /** Receita financeira do trimestre: entra 100% na base do IRPJ/CSLL (LP). */
    receita_financeira_tri?: number;
    /** De onde veio o faturamento do agente: ETL do Domínio ou importação. */
    fonte_faturamento?: 'dominio' | 'importacao' | null;
    /** Mesma base só com o Domínio (sem os ajustes dos XML). */
    faturamento_dominio?: number;
    lucro_real?: boolean;
    aliquota_pis?: number;
    aliquota_cofins?: number;
    /** Crédito de PIS/COFINS apurado no Domínio (Lucro Real). */
    credito_pis?: number;
    credito_cofins?: number;
    retencoes_sofridas?: IRetencoesSofridas;
    retencoes_sofridas_tri?: IRetencoesSofridas;
    icms_dominio?: number;
    icms_ajuste_xml?: number;
    /** XML × Domínio da raiz no mês (backend: services/agente_conferencia.py). */
    xml?: IResumoXml | null;
    irpj_acumulado: number;
    csll_acumulado: number;
    irpj_anterior: number;
    csll_anterior: number;
  };
}

export interface IRetencoesSofridas {
  pis?: number;
  cofins?: number;
  csll?: number;
  irrf?: number;
  inss?: number;
}

export interface INotaXml {
  tipo: string | null;
  numero: string | null;
  data: string | null;
  contraparte: string | null;
  valor_dominio: number | null;
  valor_xml: number | null;
  motivo: 'so_xml' | 'cancelada_so_xml' | 'valor_divergente';
  ajuste: number;
  diferenca: number | null;
  external_id?: string;
}

export interface IResumoXml {
  /** xml: calculado; sem_xml: nenhuma nota em XML; pendente: ainda calculando. */
  status: 'xml' | 'sem_xml' | 'pendente';
  calculado_em: string | null;
  ajuste_saida: number;
  ajuste_servico: number;
  ajuste_icms: number;
  retencoes_sofridas: IRetencoesSofridas;
  qtd: Record<string, number>;
  valores?: Record<string, number>;
  notas: INotaXml[];
  notas_total: number;
  duplicado_de: string[];
}

export interface IMotivosDivergencia {
  difal?: {
    agente: number;
    dominio: number;
    qtd_notas: number;
    qtd_entrada: number;
    qtd_sem_icms_destacado: number;
    sem_inscricao_estadual: boolean;
  };
  /** Receita financeira que o agente somou no IRPJ/CSLL e o Domínio não. */
  receita_financeira_irpj?: { agente: number; dominio: number };
}

export interface ICompanyQuebrasTotais {
  alertas?: number;
  quebras?: number;
  sem_quebras?: number;
}

export interface ISaida {
  cnae_descricao: any;
  verificado_sieg: boolean;
  verificado_dominio: boolean;
  sigl_est: string;
  codi_emp: string;
  nume_sai: string;
  seri_sai: string;
  codi_esp: string;
  codi_acu: string;
  dsai_sai: any;
  data_emissao: any;
  dser_ser: any;
  ddoc_ser: any;
  novo_ecf: string;
  valor_contabil: number;
  chave_nfe: string;
  external_id: string;
  external_id_column: string;
  intermunicipal: number;
  tributo: ITributo;
  empresa_id: number;
}

export interface ISaidaImport {
  id?: number;
  name: string;
  origin: string;
  quantity_nfe: number;
  quantity_nfce: number;
  quantity_nfse: number;
  quantity_fatura?: number;
  quantity_total?: number;
}

export interface ITributo {
  iss_retido: number;
  valor_iss: number;
  inss: number;
  csll: number;
  pis: number;
  cofins: number;
  valor_nfe: number;
}

interface ICompanyFilter {
  companyId: number;
}

export interface ITaxRegime {
  id: number;
  external_id: string;
  razao_social: string;
  cnpj: string;
  regime_tributario: string;
  tags?: string | null;
  faturamento_acumulado: number;
  faixa_atual: string;
  limite_restante: number;
  limite_atingido: number;
  empresa_id?: number;
}

export interface IMonthlyFaturamento {
  mes: number;
  faturamento: number;
  acumulado: number;
}

export interface IPartner {
  socio_id: string;
  nome_socio: string;
  inscricao: string;
  faturamento_acumulado: number;
  empresas_simples?: number;
  empresas_presumido?: number;
}

export interface IVarianciaNota {
  codi_emp: string | null;
  codi_esp: number | null;
  nome_empresa: string | null;
  saidas_anterior: number;
  saidas_atual: number;
  variacao_saidas: number;
  servicos_anterior: number;
  servicos_atual: number;
  variacao_servicos: number;
  entradas_anterior: number;
  entradas_atual: number;
  variacao_entradas: number;
  notas_total_anterior: number;
  notas_total_atual: number;
  variacao_notas_total: number;
}

export interface IVarianciaNotaKpi {
  ok: number;
  aviso: number;
  erro: number;
}

export interface INotasManualPosting {
  numero: string;
  especie: string;
  empresa: {
    external_id: string;
    nome: string;
    cnpj: string;
    razao_social: string;
  };
}

export interface ICompanyPayRoll {
  id: 0;
  dt_criacao: any;
  dt_atualizacao: any;
  nome: string;
  cnpj: string;
  razao_social: string;
  external_id: string;
  fgts_guia: number | null;
  fgts_calculo: number;
  fgts_grrf_calculo: number;
  total_guia_imp: number | null;
  total_calc_imp: number;
  calc_total_anterior: number;
  calc_total_atual: number;
  guia_total_anterior: number;
  guia_total_atual: number;
  variacao: number;
  recalculos: number;
  qnt_vidas: number;
  qnt_admissoes: number;
  qnt_demissoes: number;
  qnt_ferias: number;
  qnt_afastados: number;
  qnt_pro_labores: number;
  qnt_transferidos: number;
  total_fgts_atual: number;
  total_fgts_anterior: number;
  total_dctfweb_atual: number;
  total_dctfweb_anterior: number;
  variacao_fgts: number;
  variacao_guia: number;
  diferenca_dctfweb_perc: number;
  diferenca_fgts_perc: number;
  dctfweb_pdf: string | null;
  fgts_pdf: string | null;
  extrato_pdf: string | null;
}

export interface IBreakRange {
  numero_inicial: number;
  numero_final: number;
  serie: string;
  codi_esp: number;
}

export interface INotaTotalJettax {
  qt_notas: number | null;
  dt_total: string | null;
  codi_emp: string | null;
  refrencia_unica: string | null;
  tipo_nota: string | null;
  empresa_id: number | null;
}

// Interfaces para detalhamento da empresa
export interface IEmpresaSocio {
  nome_socio: string;
  inscricao: string;
  participacao: number;
  qt_cotas: number;
  data_ingresso: string;
}

export interface IEmpresaDetalhada {
  id: number;
  external_id: string;
  nome: string;
  cnpj: string;
  razao_social: string;
  cidade: string;
  uf: string;
  dt_cli_ate: string | null;
  dt_cli_desde: string | null;
  ativa: boolean;
  regime_tributario_atualizado: string;
  socios: IEmpresaSocio[];
}

export interface IFaturamentoData {
  servico: number;
  saida: number;
  outros: number;
  total: number;
}

export interface IImpostosData {
  pis: number;
  cofins: number;
  irpj: number;
  csll: number;
  icms: number;
  iss_normal: number;
  iss_retido: number;
  iss_total: number;
  simples_nacional: number;
}

export interface IFaturamentoMes {
  mes: number;
  mes_nome: string;
  faturamento: IFaturamentoData;
  impostos: IImpostosData;
}

export interface IFaturamentoImpostos {
  ano: number;
  meses: IFaturamentoMes[];
  totais: {
    faturamento: IFaturamentoData;
    impostos: IImpostosData;
  };
}

export interface INotasData {
  nfe: number;
  nfce: number;
  nfse: number;
  cte: number;
  total: number;
}

export interface INotasQuebrasMes {
  mes: number;
  mes_nome: string;
  notas: INotasData;
  quebras: INotasData;
}

export interface INotasQuebras {
  ano: number;
  meses: INotasQuebrasMes[];
  totais: {
    notas: INotasData;
    quebras: INotasData;
  };
}

export interface IFolhaPagamentoMes {
  mes: number;
  mes_nome: string;
  admissoes: number;
  demissoes: number;
  ferias: number;
  afastamentos: number;
  fgts: number;
  dctfweb: number;
}

export interface IFolhaPagamento {
  ano: number;
  qnt_vidas: number;
  qnt_prolabore: number;
  meses: IFolhaPagamentoMes[];
  totais: {
    admissoes: number;
    demissoes: number;
    ferias: number;
    afastamentos: number;
    fgts: number;
    dctfweb: number;
  };
}

export const useCompanies = () => {
  const getCompanies = async (key: QueryFunctionContext): Promise<IData<ICompany[]>> => {
    const filters = (key.queryKey?.[1] ?? {}) as Record<string, any>;
    // Timeout maior para telas com filtros pesados (simples_nacional, lucro_real, etc.)
    const needsLongerTimeout =
      filters.screen_data === 'simples_nacional' ||
      filters.screen_data === 'lucro_real' ||
      filters.screen_data === 'lucro_presumido';
    const res = await api.get<IData<ICompany[]>>('/api/v1/empresas/', {
      params: filters,
      timeout: needsLongerTimeout ? 300000 : undefined, // 5 minutos para telas pesadas
    });
    return res.data;
  };

  const getCompaniesDash = async (key: QueryFunctionContext): Promise<IData<ICompany[]>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IData<ICompany[]>>('/api/v1/empresa-dash/empresas_list/', {
      params: filters,
    });
    return res.data;
  };

  const getSituationsTotals = async (
    key: QueryFunctionContext,
  ): Promise<IData<ICompaniesSituations>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IData<ICompaniesSituations>>('/api/v1/empresas/faturamento/total/', {
      params: filters,
    });
    return res.data;
  };
  const getSituationsReceitasTotals = async (
    key: QueryFunctionContext,
  ): Promise<IData<ICompaniesSituations>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IData<ICompaniesSituations>>('/api/v1/empresas/receitas/total/', {
      params: filters,
    });
    return res.data;
  };

  const getCompany = async (key: QueryFunctionContext): Promise<IData<ICompany>> => {
    const { companyId } = key.queryKey[1] as ICompanyFilter;
    const res = await api.get<IData<ICompany>>(`/api/v1/empresas/${companyId}/`);
    return res.data;
  };

  const createCompany = async (data: ICompany): Promise<IData<ICompany>> => {
    const res = await api.post<IData<ICompany>>('/api/v1/empresas/', data);
    return res.data;
  };

  const updateCompany = async (data: ICompany): Promise<IData<ICompany>> => {
    const res = await api.put<IData<ICompany>>(`/api/v1/empresas/${data.id}/`, data);
    return res.data;
  };

  const deleteCompany = async (companyId: string) => {
    const res = await api.delete(`/api/v1/empresas/${companyId}/`);
    return res.data;
  };

  const getQuebras = async (key: QueryFunctionContext): Promise<IData<ICompany[]>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IData<ICompany[]>>('/api/v1/empresas/quebras/', {
      params: filters,
    });
    return res.data;
  };

  const getQuebrasWithId = async (key: QueryFunctionContext): Promise<IData<IBreakRange[]>> => {
    const filters = key.queryKey?.[1] ?? ({} as unknown as any);
    const res = await api.get<IData<IBreakRange[]>>(
      `/api/v1/empresas/${filters?.companyId}/quebras/`,
      {
        params: _.omit(filters, 'companyId'),
      },
    );
    return res.data;
  };

  const getQuebrasTotais = async (key: QueryFunctionContext): Promise<any> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<any>('/api/v1/empresas/quebras/total/', {
      params: filters,
    });
    return res.data;
  };
  const getAPIXSiegTotais = async (key: QueryFunctionContext): Promise<IApiSiegTotais> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IApiSiegTotais>('/api/v1/empresas/api_sieg/total/', {
      params: filters,
    });
    return res.data;
  };
  const getFaturamentoTotais = async (key: QueryFunctionContext): Promise<any> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<any>('/api/v1/empresas/faturamento/total/', {
      params: filters,
    });
    return res.data;
  };

  const uploadCompanyFile = async ({
    file,
  }: {
    file: HTMLInputElement;
  }): Promise<IDataNoPagination<ICompany>> => {
    const formData = new FormData();
    formData.append('file', file[0]);
    const res = await api.post('/api/v1/empresas/import/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data', // Ensure correct Content-Type header for FormData
      },
    });
    return res.data;
  };

  const getCompaniesFilters = async (key?: QueryFunctionContext) => {
    const params = key?.queryKey?.[1] as { incluir_excluidos?: boolean } | undefined;
    const res = await api.get(`/api/v1/empresas/filters/`, {
      params: params || {},
    });
    return res.data;
  };

  const getCompaniesSaidas = async (key: QueryFunctionContext): Promise<IData<ISaida[]>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IData<ISaida[]>>('/api/v1/saidas/', {
      params: filters,
    });
    return res.data;
  };

  const getCompaniesSaidasImports = async (
    key: QueryFunctionContext,
  ): Promise<IData<ISaidaImport[]>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IData<ISaidaImport[]>>('/api/v1/saidas/importacao/', {
      params: filters,
    });
    return res.data;
  };

  const getSaidasImportsTotais = async (key: QueryFunctionContext): Promise<any> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<any>('/api/v1/saidas/importacao/total/', {
      params: filters,
    });
    return res.data;
  };

  const getCompaniesSaidasXml = async (key: QueryFunctionContext): Promise<IData<ISaida[]>> => {
    const filters = (key.queryKey?.[1] as any) ?? {};
    const res = await api.get<IData<ISaida[]>>(`/api/v1/empresas/sieg/`, {
      params: filters,
    });
    return res.data;
  };

  const getTaxRegime = async (key: QueryFunctionContext): Promise<IData<ITaxRegime[]>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IData<ITaxRegime[]>>('/api/v1/regime_tributario/limite/', {
      params: filters,
    });
    return res.data;
  };
  const getQuebrasTaxRegimeTotais = async (key: QueryFunctionContext): Promise<any> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<any>('/api/v1/regime_tributario/limite/total/', {
      params: filters,
    });
    return res.data;
  };

  const getPartnerCompany = async (key: QueryFunctionContext): Promise<IData<IPartner[]>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IData<IPartner[]>>('/api/v1/regime_tributario/limite/socios/', {
      params: filters,
    });
    return res.data;
  };

  const getQuebrasAnalisesTotais = async (key: QueryFunctionContext): Promise<any> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<any>('/api/v1/empresas/impostos/total/', {
      params: filters,
      timeout: 300000, // 5 minutos - endpoint pesado com agregações
    });
    return res.data;
  };

  // Endpoints específicos para Simples Nacional (com cache no backend)
  const getCompaniesSimplesNacional = async (
    key: QueryFunctionContext,
  ): Promise<IData<ICompany[]>> => {
    const filters = (key.queryKey?.[1] ?? {}) as Record<string, any>;
    const res = await api.get<IData<ICompany[]>>('/api/v1/empresas/simples-nacional/', {
      params: filters,
      timeout: 300000,
    });
    return res.data;
  };

  const getTotaisSimplesNacional = async (key: QueryFunctionContext): Promise<any> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<any>('/api/v1/empresas/simples-nacional/total/', {
      params: filters,
      timeout: 300000,
    });
    return res.data;
  };

  const getNotasManualPosting = async (
    key: QueryFunctionContext,
  ): Promise<IData<INotasManualPosting[]>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IData<INotasManualPosting[]>>('/api/v1/saidas/importacao/notas/', {
      params: filters,
    });
    return res.data;
  };

  const getFolhaTotais = async (key: QueryFunctionContext): Promise<any> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<any>('/api/v1/empresas/folha/total/', {
      params: filters,
    });
    return res.data;
  };

  const getCompaniesFolha = async (key: QueryFunctionContext): Promise<any> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<any>('/api/v1/empresas/folha/', {
      params: filters,
    });
    return res.data;
  };

  const getMonthlyFaturamento = async (
    key: QueryFunctionContext,
  ): Promise<IMonthlyFaturamento[]> => {
    const filters = key.queryKey?.[1] as { external_id: string; year: number };
    const res = await api.get<IMonthlyFaturamento[]>(
      '/api/v1/regime_tributario/limite/faturamento-mensal/',
      {
        params: filters,
      },
    );
    return res.data;
  };

  const getNotasTotaisJettax = async (
    key: QueryFunctionContext,
  ): Promise<IData<INotaTotalJettax[]>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IData<INotaTotalJettax[]>>(
      '/api/v1/integracoes/notas-totais-jettax/',
      { params: filters },
    );
    return res.data;
  };

  // Funções para detalhamento da empresa
  const getEmpresaDetalhada = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IEmpresaDetalhada>> => {
    const { externalId } = key.queryKey[1] as { externalId: string };
    const res = await api.get<IDataNoPagination<IEmpresaDetalhada>>(
      `/api/v1/empresa-detalhamento/${externalId}/`,
    );
    return res.data;
  };

  const getEmpresaFaturamentoImpostos = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IFaturamentoImpostos>> => {
    const { externalId, ano } = key.queryKey[1] as { externalId: string; ano: number };
    const res = await api.get<IDataNoPagination<IFaturamentoImpostos>>(
      `/api/v1/empresa-detalhamento/${externalId}/faturamento-impostos/`,
      { params: { ano } },
    );
    return res.data;
  };

  const getEmpresaNotasQuebras = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<INotasQuebras>> => {
    const { externalId, ano } = key.queryKey[1] as { externalId: string; ano: number };
    const res = await api.get<IDataNoPagination<INotasQuebras>>(
      `/api/v1/empresa-detalhamento/${externalId}/notas-quebras/`,
      { params: { ano } },
    );
    return res.data;
  };

  const getEmpresaFolhaPagamento = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IFolhaPagamento>> => {
    const { externalId, ano } = key.queryKey[1] as { externalId: string; ano: number };
    const res = await api.get<IDataNoPagination<IFolhaPagamento>>(
      `/api/v1/empresa-detalhamento/${externalId}/folha-pagamento/`,
      { params: { ano } },
    );
    return res.data;
  };

  const updateNivelEmpresa = async (data: {
    companyId: number;
    nivel: NivelEmpresa;
  }): Promise<IDataNoPagination<ICompany>> => {
    const res = await api.put<IDataNoPagination<ICompany>>(
      `/api/v1/empresas/${data.companyId}/nivel/`,
      {
        nivel: data.nivel,
      },
    );
    return res.data;
  };

  const getVarianciasNotas = async (
    key: QueryFunctionContext,
  ): Promise<IData<IVarianciaNota[]>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IData<IVarianciaNota[]>>('/api/v1/integracoes/variancia-notas/', {
      params: filters,
    });
    return res.data;
  };

  const getVarianciasNotasKpi = async (
    key: QueryFunctionContext,
  ): Promise<{ data: IVarianciaNotaKpi }> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<{ data: IVarianciaNotaKpi }>(
      '/api/v1/integracoes/variancia-notas/kpi',
      { params: filters },
    );
    return res.data;
  };

  const getCompaniesStats = async (): Promise<{
    total: number;
    ativas: number;
    ativas_periodo: number;
    processo_off: number;
    inativas: number;
    inativas_periodo: number;
    fora_do_acessorias: number;
    fora_da_dominio: number;
    divergencias: number;
    divergencias_ativas: number;
    divergencias_inativas: number;
  }> => {
    const res = await api.get('/api/v1/empresas/stats/');
    return res.data;
  };

  // Função para calcular IA de uma empresa
  const calcularIAEmpresa = async (data: ICalculoIARequest): Promise<ICalculoIAResponse> => {
    const res = await api.post<ICalculoIAResponse>('/api/v1/chat/calculo-ia-empresa', data);
    return res.data;
  };

  const uploadEmpresaLogo = async ({ empresaId, file }: { empresaId: number; file: File }) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post(`/api/v1/empresas/${empresaId}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  };

  const deleteEmpresaLogo = async (empresaId: number) => {
    const res = await api.delete(`/api/v1/empresas/${empresaId}/logo`);
    return res.data;
  };

  return {
    getCompanies,
    getSituationsTotals,
    calcularIAEmpresa,
    getCompany,
    getQuebras,
    getQuebrasTotais,
    createCompany,
    updateCompany,
    deleteCompany,
    uploadCompanyFile,
    getQuebrasWithId,
    getCompaniesFilters,
    getCompaniesSaidas,
    getCompaniesSaidasImports,
    getSaidasImportsTotais,
    getCompaniesSaidasXml,
    getAPIXSiegTotais,
    getFaturamentoTotais,
    getTaxRegime,
    getPartnerCompany,
    getQuebrasTaxRegimeTotais,
    getQuebrasAnalisesTotais,
    getCompaniesSimplesNacional,
    getTotaisSimplesNacional,
    getNotasManualPosting,
    getSituationsReceitasTotals,
    getFolhaTotais,
    getCompaniesFolha,
    getCompaniesDash,
    getMonthlyFaturamento,
    getNotasTotaisJettax,
    getEmpresaDetalhada,
    getEmpresaFaturamentoImpostos,
    getEmpresaNotasQuebras,
    getEmpresaFolhaPagamento,
    updateNivelEmpresa,
    getCompaniesStats,
    getVarianciasNotas,
    getVarianciasNotasKpi,
    uploadEmpresaLogo,
    deleteEmpresaLogo,
  };
};
